import React, { FC } from 'react';
import PollEntity from '../../../store/polls-store/poll-entity';
import MoveHandleIcon from '../../shared/move-handle-icon/move-handle-icon';
import { PollsTabsOnMobile, PollsViewStatuses } from '../../../constans/polls-constants';
import HideForPresenter from '../shared/hide-for-presenter';
import OpenCloseButton from '../shared/open-close-button';
import { observer } from 'mobx-react';
import PollStatus from '../shared/poll-status/poll-status';
import classNames from 'classnames';
import ElipsisMenuWrapper from '../shared/elipsis-menu-wrapper';
import ResultSlideButton from '../shared/result-slide-button';
import ShowResultsButton from '../shared/show-results-button';

interface IProps {
  poll: PollEntity;
  isSelected: boolean;
  isEllipsisMenuOpened: boolean;
  setShouldShowEllipsisMenu: (value: boolean) => void;
  setShouldShowDeleteModal: (value: boolean) => void;
  setShouldShowAddResultsSlideModal: (value: boolean) => void;
  setShouldShowResetModal: (value: boolean) => void;
  toggleManagePollModal: () => void;
  changePollStatus: (poll: PollEntity, customStatus?: PollsViewStatuses) => Promise<void>;
  setActiveMobileTab: (tab: PollsTabsOnMobile) => void;
  isMobile: boolean;
  setIsOnePollOpened: (value: boolean) => void;
  isOnePollOpened: boolean;
  isSomePollResultsShared: boolean;
  isNextGenPlayer: boolean;
  sharePollResults: (poll: PollEntity, resultsShared: boolean) => Promise<void>;
}

const Poll: FC<IProps> = ({
  poll,
  isSelected,
  isEllipsisMenuOpened,
  setShouldShowEllipsisMenu,
  setShouldShowDeleteModal,
  setShouldShowResetModal,
  toggleManagePollModal,
  changePollStatus,
  setActiveMobileTab,
  isMobile,
  isOnePollOpened,
  isSomePollResultsShared,
  isNextGenPlayer,
  sharePollResults,
  setShouldShowAddResultsSlideModal,
}) => {
  const pollItemClassName = `poll-item ${isSelected ? 'selected' : ''}`;
  const { status, type } = poll;
  const shouldShowOpenCloseBtn = status !== PollsViewStatuses.COMPLETED && status !== PollsViewStatuses.RESETED;
  // Legacy's Add Results Slide button — unchanged rule, and it still drives the layout classes below.
  const shouldShowResultSlideBtn = status === PollsViewStatuses.COMPLETED;
  // Next-Gen's Show/Hide Results toggle needs at least one attendee response: there is nothing to share
  // from a closed poll nobody answered.
  const shouldShowShareResultsBtn = shouldShowResultSlideBtn && poll.countVotes > 0;
  // Locked out while another poll is currently sharing its results (Next-Gen live/published only).
  // This list view never disabled Open Poll based on isOnePollOpened before this feature — keep that
  // untouched for Legacy and only add the new results-sharing lock, which is always false for Legacy.
  const isResultsSharingLockedByOtherPoll = isNextGenPlayer && isSomePollResultsShared && !poll.resultsShared;
  const isOpenButtonDisabled = status === PollsViewStatuses.NOT_STARTED && isResultsSharingLockedByOtherPoll;
  // This poll's own ellipsis menu locks down entirely while it's the one sharing results.
  const isActivePollSharingResults = isNextGenPlayer && poll.resultsShared;

  const changeStatusHandler = (): void => {
    changePollStatus(poll);
  };

  return (
    <div className={pollItemClassName}>
      <MoveHandleIcon/>
      <div className='poll-item-header'>
        <HideForPresenter hideForPresenter={poll.hideForPresenter}/>
        <ElipsisMenuWrapper
          poll={poll}
          isEllipsisMenuOpened={isEllipsisMenuOpened && isSelected}
          setShouldShowEllipsisMenu={setShouldShowEllipsisMenu}
          setShouldShowDeleteModal={setShouldShowDeleteModal}
          setShouldShowResetModal={setShouldShowResetModal}
          toggleManagePollModal={toggleManagePollModal}
          setActiveMobileTab={setActiveMobileTab}
          changePollStatus={changePollStatus}
          isMobile={isMobile}
          isOnePollOpened={isOnePollOpened}
          isResultsSharingLockedByOtherPoll={isResultsSharingLockedByOtherPoll}
          isActivePollSharingResults={isActivePollSharingResults}
        />

      </div>
      <p className='poll-item-question'>{poll.value}</p>
      <span className='poll-item-type'>{type}</span>
      <div className='poll-item-info'>
        <div
          className={classNames('votes-status-container',
            {
              'row-direction': status === PollsViewStatuses.COMPLETED && !shouldShowResultSlideBtn,
              'content-start': status === PollsViewStatuses.NOT_STARTED,
              'column-direction': shouldShowResultSlideBtn
            })}
        >
          <PollStatus status={status} additionalClassName='poll-item-status'/>
          {status !== PollsViewStatuses.NOT_STARTED && <span className='poll-item-votes'>{poll.countVotes} Votes</span>}
        </div>
        {shouldShowOpenCloseBtn && <OpenCloseButton disabled={isOpenButtonDisabled} onClick={changeStatusHandler} status={status}/>}
        {isNextGenPlayer
          ? shouldShowShareResultsBtn && (
            <ShowResultsButton
              resultsShared={poll.resultsShared}
              disabled={isOnePollOpened || isResultsSharingLockedByOtherPoll}
              onClick={() => sharePollResults(poll, !poll.resultsShared)}
            />
          )
          : shouldShowResultSlideBtn && (
            <ResultSlideButton
              openConfirmationModal={setShouldShowAddResultsSlideModal}
              showButton={!poll.result_slide_added}
              isLoading={poll.shouldShowGenerating}
            />
          )
        }
      </div>
    </div>
  );
};

export default observer(Poll);
